import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Avatar, Space, Typography, message, Popconfirm, Modal, Empty, Input, Switch, Tag, Grid } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ShoppingOutlined, EyeOutlined, CopyOutlined, SearchOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Product, ProductStatus } from '../../types';
import { productsService } from '../../services/products.service';
import config from '../../config';

// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbs, FreeMode, Navigation } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const ProductList: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [viewProduct, setViewProduct] = useState<Product | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const getImageUrl = (url?: string) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const formattedUrl = url.startsWith('/') ? url : `/${url}`;
        return `${config.BASE_URL}${formattedUrl}`;
    };

    const fetchProducts = async (search?: string) => {
        setLoading(true);
        try {
            const data = await productsService.getAll(search);
            setProducts(data);
        } catch (error) {
            console.error('Failed to fetch products', error);
            message.error('Không thể tải danh sách sản phẩm');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        fetchProducts(value);
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleDelete = async (id: string) => {
        try {
            await productsService.delete(id);
            message.success('Đã xóa sản phẩm');
            fetchProducts();
        } catch (error) {
            console.error('Xóa thất bại', error);
            message.error('Không thể xóa sản phẩm');
        }
    };

    const handleStatusChange = async (id: string, checked: boolean) => {
        const newStatus = checked ? ProductStatus.IN_STOCK : ProductStatus.OUT_OF_STOCK;
        try {
            await productsService.update(id, { status: newStatus });
            message.success('Đã cập nhật tình trạng sản phẩm');
            setProducts(prev => prev.map(p => p._id === id ? { ...p, status: newStatus } : p));
        } catch (error) {
            console.error('Update status failed', error);
            message.error('Không thể cập nhật tình trạng');
        }
    };

    const columns = [
        {
            title: 'Hình ảnh',
            key: 'image',
            width: 100,
            render: (_: any, record: Product) => {
                const primaryImage = record.images.find(img => img.isPrimary) || record.images[0];
                return (
                    <Avatar 
                        src={getImageUrl(primaryImage?.url)} 
                        shape="square" 
                        size={isMobile ? 60 : 200} 
                        icon={<ShoppingOutlined />} 
                    />
                );
            },
        },
        {
            title: 'Tên sản phẩm',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (text: string) => <span style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>{text}</span>,
        },
        {
            title: 'Tình trạng',
            key: 'status',
            width: 130,
            render: (_: any, record: Product) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    <Switch 
                        size="small"
                        checked={record.status === ProductStatus.IN_STOCK}
                        onChange={(checked) => handleStatusChange(record._id, checked)}
                    />
                    {record.status === ProductStatus.IN_STOCK ? (
                        <Tag 
                            color="success" 
                            icon={<CheckCircleOutlined />}
                            style={{ borderRadius: 12, padding: '0 8px', margin: 0, fontSize: 11 }}
                        >
                            Còn hàng
                        </Tag>
                    ) : (
                        <Tag 
                            color="error" 
                            icon={<StopOutlined />}
                            style={{ borderRadius: 12, padding: '0 8px', margin: 0, fontSize: 11 }}
                        >
                            Hết hàng
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Giá',
            dataIndex: 'price',
            key: 'price',
            width: 110,
            render: (price: number) => (
                <Text strong style={{ color: '#cf1322', fontSize: isMobile ? 14 : 16 }}>
                    {price ? `${price.toLocaleString()}` : ''}
                </Text>
            ),
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date: string) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {date ? new Date(date).toLocaleString('vi-VN', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : '-'}
                </Text>
            ),
        },
        {
            title: 'Ngày cập nhật',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            render: (date: string) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {date ? new Date(date).toLocaleString('vi-VN', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : '-'}
                </Text>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: (isMobile ? 'right' : undefined) as any,
            width: 120,
            render: (_: any, record: Product) => (
                <Space size="small">
                    <Button 
                        size="small"
                        icon={<EyeOutlined />} 
                        onClick={() => {
                            setViewProduct(record);
                            setIsModalVisible(true);
                        }}
                    />
                    <Button 
                        size="small"
                        type="primary" 
                        icon={<EditOutlined />} 
                        onClick={() => navigate(`/products/${record._id}`)}
                    />
                    <Popconfirm
                        title="Xóa sản phẩm?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Xóa"
                        cancelText="Hủy"
                        okButtonProps={{ danger: true }}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Space direction="vertical" size={isMobile ? "middle" : "large"} style={{ width: '100%' }}>
            <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 16
            }}>
                <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Sản phẩm</Title>
                <div style={{ display: 'flex', gap: 12, width: isMobile ? '100%' : 'auto' }}>
                    <Input.Search
                        placeholder="Tìm kiếm..."
                        onSearch={handleSearch}
                        onChange={(e) => {
                            if (!e.target.value) handleSearch('');
                        }}
                        style={{ flex: 1, minWidth: isMobile ? 0 : 300 }}
                        enterButton
                    />
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={() => navigate('/products/create')}
                        style={{ flexShrink: 0 }}
                    >
                        {isMobile ? '' : 'Thêm'}
                    </Button>
                </div>
            </div>

            <Card bodyStyle={{ padding: isMobile ? '0' : '24px' }}>
                <Table 
                    columns={columns} 
                    dataSource={products} 
                    rowKey="_id" 
                    loading={loading} 
                    locale={{ emptyText: 'Chưa có sản phẩm nào' }}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        size: isMobile ? 'small' : undefined,
                        pageSize: 10,
                    }}
                />
            </Card>

            <Modal
                title={viewProduct?.name || 'Chi tiết sản phẩm'}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setViewProduct(null);
                    setThumbsSwiper(null);
                }}
                footer={[
                    <Button key="close" onClick={() => setIsModalVisible(false)}>
                        Đóng
                    </Button>,
                    <Button 
                        key="edit" 
                        type="primary" 
                        icon={<EditOutlined />}
                        onClick={() => {
                            if (viewProduct) navigate(`/products/${viewProduct._id}`);
                        }}
                    >
                        Chỉnh sửa
                    </Button>
                ]}
                width={isMobile ? '95%' : 1000}
                destroyOnClose
                centered
            >
                {viewProduct && (
                    <div style={{ padding: isMobile ? '10px 0' : '20px 0' }}>
                        <div style={{ display: 'flex', gap: isMobile ? 16 : 32, flexDirection: isMobile ? 'column' : 'row' }}>
                            <div style={{ flex: isMobile ? '1' : '0 0 450px', maxWidth: '100%', overflow: 'hidden' }}>
                                {viewProduct.images && viewProduct.images.length > 0 ? (
                                    <div className="product-swiper-container">
                                        <Swiper
                                            spaceBetween={10}
                                            navigation={!isMobile}
                                            thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                                            modules={[FreeMode, Navigation, Thumbs]}
                                            className="main-swiper"
                                            style={{
                                                '--swiper-navigation-color': '#fff',
                                                '--swiper-pagination-color': '#fff',
                                                borderRadius: 12,
                                                overflow: 'hidden',
                                                height: isMobile ? 300 : 400,
                                                background: '#f8f8f8'
                                            } as React.CSSProperties}
                                        >
                                            {viewProduct.images.map((img, idx) => (
                                                <SwiperSlide key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <img 
                                                        src={getImageUrl(img.url)} 
                                                        alt={viewProduct.name}
                                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                    />
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                        
                                        <Swiper
                                            onSwiper={setThumbsSwiper}
                                            spaceBetween={8}
                                            slidesPerView={isMobile ? 3 : 4}
                                            freeMode={true}
                                            watchSlidesProgress={true}
                                            modules={[FreeMode, Navigation, Thumbs]}
                                            className="thumbs-swiper"
                                            style={{ marginTop: 8, height: isMobile ? 70 : 90, padding: '4px' }}
                                        >
                                            {viewProduct.images.map((img, idx) => (
                                                <SwiperSlide key={idx} className="thumb-item-slide">
                                                    <div className="thumb-inner">
                                                        <img 
                                                            src={getImageUrl(img.url)} 
                                                            alt={`${viewProduct.name} thumbnail ${idx}`}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    </div>
                                ) : (
                                    <Empty description="Không có hình ảnh" />
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <Title level={isMobile ? 4 : 2} style={{ marginBottom: 8 }}>{viewProduct.name}</Title>
                                <Text type="danger" style={{ fontSize: isMobile ? 20 : 24, fontWeight: 'bold', display: 'block', marginBottom: 16 }}>
                                    {viewProduct.price ? `${viewProduct.price.toLocaleString()}` : ''}
                                </Text>
                                
                                <Card 
                                    size="small" 
                                    title="Mô tả sản phẩm" 
                                    bordered={false} 
                                    style={{ background: '#fafafa', borderRadius: 12 }}
                                    extra={
                                        viewProduct.description && !isMobile && (
                                            <Button 
                                                type="text" 
                                                size="small" 
                                                icon={<CopyOutlined />} 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(viewProduct.description as string);
                                                    message.success('Đã sao chép mô tả');
                                                }}
                                            >
                                                Sao chép
                                            </Button>
                                        )
                                    }
                                >
                                    <Paragraph style={{ whiteSpace: 'pre-wrap', color: '#595959', fontSize: isMobile ? 13 : 15, lineHeight: 1.6 }}>
                                        {viewProduct.description || 'Chưa có mô tả cho sản phẩm này.'}
                                    </Paragraph>
                                </Card>
                                
                                {viewProduct.quickMessages && viewProduct.quickMessages.length > 0 && (
                                    <div style={{ marginTop: 20 }}>
                                        <Title level={5} style={{ marginBottom: 10 }}>Tin nhắn nhanh</Title>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {viewProduct.quickMessages.map((msg, idx) => (
                                                <Card 
                                                    key={idx} 
                                                    size="small" 
                                                    bodyStyle={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                    style={{ borderRadius: 8, border: '1px solid #f0f0f0' }}
                                                >
                                                    <Text style={{ flex: 1, marginRight: 8, fontSize: isMobile ? 12 : 14 }}>{msg}</Text>
                                                    <Button 
                                                        type="text" 
                                                        size="small"
                                                        icon={<CopyOutlined />} 
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(msg);
                                                            message.success('Đã sao chép tin nhắn');
                                                        }}
                                                    />
                                                </Card>
                                            ))}
                                        </Space>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <style>{`
                .product-swiper-container {
                    width: 100%;
                }
                .main-swiper {
                    width: 100%;
                    border: 1px solid #f0f0f0;
                }
                .thumbs-swiper {
                    overflow: visible !important;
                    padding: 8px 4px !important;
                }
                .thumb-item-slide {
                    cursor: pointer;
                    opacity: 0.6;
                    transition: all 0.3s;
                    height: ${isMobile ? '60px' : '80px'};
                }
                .thumb-inner {
                    width: 100%;
                    height: 100%;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 2px solid transparent;
                    transition: all 0.3s;
                    padding: 2px;
                    background: #fff;
                }
                .thumb-item-slide.swiper-slide-thumb-active {
                    opacity: 1 !important;
                }
                .thumb-item-slide.swiper-slide-thumb-active .thumb-inner {
                    border-color: #1890ff;
                }
                .thumb-item-slide:hover {
                    opacity: 0.9;
                }
                
                .swiper-button-next, .swiper-button-prev {
                    background: rgba(255, 255, 255, 0.8);
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    color: #000 !important;
                }
                .swiper-button-next:after, .swiper-button-prev:after {
                    font-size: 12px !important;
                    font-weight: bold;
                }
            `}</style>
        </Space>
    );
};

export default ProductList;
